/**
 * Subscribe to a church's calendar once, and new services arrive on their own.
 *
 * The existing "add all to my calendar" link downloads a snapshot: the phone
 * copies today's entries in and a service added next week never appears. This
 * hands over a **feed** instead (`webcal://…/subscribe.ics`), which the phone
 * re-reads twice a day. Nothing personal is in it and it needs no login, so
 * the same link works as a QR code on a notice sheet.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { Check, Copy, QrCode, Rss, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SubscribeCalendar = ({ slug, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');

  const { data } = useQuery({
    queryKey: ['calendar-subscribe', slug],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/public/church-calendar/${slug}/subscribe`);
      if (!r.ok) throw new Error('Subscription link unavailable');
      return r.json();
    },
    enabled: !!slug,
    staleTime: 600_000,
  });

  const copy = async (what, value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const field = document.createElement('textarea');
      field.value = value;
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    setCopied(what);
    setTimeout(() => setCopied(''), 2000);
  };

  if (!data) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="calendar-subscribe-open"
              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100">
        <Rss className="h-3.5 w-3.5" /> Subscribe
      </button>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={() => setOpen(false)} data-testid="calendar-subscribe-dialog">
          <div onClick={(e) => e.stopPropagation()}
               className={`w-full max-w-md rounded-2xl p-6 ${dark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Subscribe once</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Every service and event from {data.church.name} appears in your
                  own calendar, and anything new arrives on its own
                  ({data.refresh}).
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="calendar-subscribe-close"
                      className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-2">
              <a href={data.webcal_url} data-testid="calendar-subscribe-webcal"
                 className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700">
                <Rss className="h-4 w-4" /> Add to this device
              </a>
              <a href={data.google_url} target="_blank" rel="noreferrer"
                 data-testid="calendar-subscribe-google"
                 className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium hover:bg-slate-50">
                Add to Google Calendar
              </a>
              <button onClick={() => copy('link', data.https_url)}
                      data-testid="calendar-subscribe-copy"
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium hover:bg-slate-50">
                {copied === 'link' ? <Check className="h-4 w-4 text-emerald-600" />
                                   : <Copy className="h-4 w-4" />}
                {copied === 'link' ? 'Link copied' : 'Copy the link'}
              </button>
            </div>

            <div className="mt-5 flex items-center gap-4 rounded-xl border border-slate-200 p-4">
              <div className="rounded-lg bg-white p-2" data-testid="calendar-subscribe-qr">
                <QRCode value={data.https_url} size={96} />
              </div>
              <p className="text-xs text-slate-500">
                <QrCode className="mb-1 inline h-3.5 w-3.5" /> Point a phone
                camera here to subscribe — the same code can go on a notice
                sheet or a screen at the back of the hall.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscribeCalendar;
